import { useEffect, useRef, useState } from "react";
import { runInAction } from "mobx";
import { observer } from "mobx-react-lite";

import { ShareSheetModalStore } from "@/components/modal/share-sheet/share-sheet-modal-store";
import { ShareSheetInviteToMemModal } from "@/components/modal/share-sheet/share-sheet-invite-to-mem-modal";
import { ShareSheetEntityKind } from "@/components/modal/share-sheet/types";
import { ApiSchema, api } from "@/modules/api";
import { CustomEventName, useCustomEvent } from "@/modules/custom-event";
import { toastModule } from "@/modules/toast";

const sharedItemKindMap: Record<ShareSheetEntityKind, ApiSchema["SharedItemKind"]> = {
  [ShareSheetEntityKind.Note]: "NOTE",
  [ShareSheetEntityKind.Collection]: "COLLECTION",
  [ShareSheetEntityKind.Template]: "UNKNOWN",
};

interface ShareSheetInviteModalManagerProps {
  store: ShareSheetModalStore;
}

export const ShareSheetInviteModalManager = observer(
  ({ store }: ShareSheetInviteModalManagerProps) => {
    const [isSendingInvite, setIsSendingInvite] = useState(false);
    const [isCooldown, setIsCooldown] = useState(false);
    const pendingTimeoutsRef = useRef<number[]>([]);
    const cooldownTimeoutRef = useRef<number | null>(null);

    useCustomEvent(CustomEventName.ShareFailed, ({ email }) => {
      if (!store.isOpen) return;
      runInAction(() => {
        store.isSharing = true;
      });
      const timeoutId = window.setTimeout(() => {
        runInAction(() => {
          store.enqueueFailedInvite(email);
          store.isAwaitingFailureModal = true;
          store.isSharing = false;
        });
        pendingTimeoutsRef.current = pendingTimeoutsRef.current.filter(id => id !== timeoutId);
      }, 1000);
      pendingTimeoutsRef.current.push(timeoutId);
    });

    useEffect(() => {
      return () => {
        pendingTimeoutsRef.current.forEach(timeoutId => window.clearTimeout(timeoutId));
        pendingTimeoutsRef.current = [];
        if (cooldownTimeoutRef.current) {
          window.clearTimeout(cooldownTimeoutRef.current);
          cooldownTimeoutRef.current = null;
        }
        runInAction(() => {
          store.isAwaitingFailureModal = false;
          store.isSharing = false;
        });
      };
    }, [store]);

    const email = store.failedInviteEmail;
    const isOpen = Boolean(email) && !isCooldown;

    const startCooldown = () => {
      setIsCooldown(true);
      if (cooldownTimeoutRef.current) window.clearTimeout(cooldownTimeoutRef.current);
      cooldownTimeoutRef.current = window.setTimeout(() => {
        setIsCooldown(false);
        cooldownTimeoutRef.current = null;
        runInAction(() => {
          store.isSharing = false;
        });
      }, 800);
    };

    const handleCancel = () => {
      if (isSendingInvite) return;
      runInAction(() => {
        store.dequeueFailedInvite();
      });
      startCooldown();
    };

    const handleSendInvite = async () => {
      if (!email) return;
      if (!store.id || !store.entityKind) {
        store.dequeueFailedInvite();
        return;
      }
      const sharedItemKind = sharedItemKindMap[store.entityKind];
      setIsSendingInvite(true);

      try {
        await new Promise(resolve => window.setTimeout(resolve, 1000));
        await api.post("/v2/shared-item-invites", {
          body: {
            shared_item_id: store.id,
            shared_item_kind: sharedItemKind,
            invitee_email: email,
            role: "EDITOR",
          },
        });

        toastModule.triggerToast({
          content: <>Invited {email} to Mem</>,
        });
        runInAction(() => {
          store.dequeueFailedInvite();
        });
        startCooldown();
      } catch (_error) {
        toastModule.triggerToast({
          content: <>Unable to invite {email} to Mem, please try again later</>,
        });
        runInAction(() => {
          store.dequeueFailedInvite();
        });
        startCooldown();
      } finally {
        setIsSendingInvite(false);
      }
    };

    return (
      <ShareSheetInviteToMemModal
        email={email ?? ""}
        entityKind={store.entityKind}
        isOpen={isOpen}
        isSendingInvite={isSendingInvite}
        onCancel={handleCancel}
        onSendInvite={handleSendInvite}
      />
    );
  }
);
